<template>
  <div class="fish-book" :class="{ 'fish-book--uma-pagina': umaPagina }">
    <!-- Os marcadores de fita (D9), no lugar das abas góticas do War Mages. -->
    <nav v-if="marcadores.length" class="fish-book__fitas" :aria-label="rotuloDosMarcadores">
      <button
        v-for="(m, i) in marcadores"
        :key="m.id"
        class="fish-book__fita"
        :class="{ 'fish-book__fita--ativa': m.id === marcador }"
        :style="{ '--cor-fita': `var(--fita-${(i % 5) + 1})` }"
        type="button"
        :aria-current="m.id === marcador ? 'true' : null"
        @click="$emit('update:marcador', m.id)"
      >
        <span class="fish-book__fita-texto">{{ m.rotulo }}</span>
      </button>
    </nav>

    <!-- O `:key` pelo marcador remonta o miolo na troca de fita — é o que dispara a animação de
         entrada das páginas: o conteúdo novo SURGE em vez de trocar num piscar seco. -->
    <div :key="marcador" class="fish-book__caderno">
      <img class="fish-book__arte" :src="arteAtual" alt="" aria-hidden="true" />

      <!--
        As áreas de página são posicionadas em PERCENTUAL sobre a arte, e os números vêm medidos —
        do QUADRO desenhado em cada folha, não da borda do papel (a conta está no CSS, em
        `.fish-book__pagina`). Recortar a arte de novo exige remedir — ver `src/assets/ui/README.md`.
      -->
      <!--
        No telefone a dupla vira UMA PÁGINA DE VERDADE (pedido do autor, 03/09): a arte é a metade
        direita do caderno, recortada (`caderno-pagina.png`, tools/assets/recortar-caderno-pagina),
        e as duas metades de conteúdo EMPILHAM nela (D3). Empilhar aqui (e não no slot de quem usa)
        poupa cada tela de escrever o mesmo conteúdo duas vezes — e cada slot renderiza UMA vez por
        estado, porque um conteúdo com fetch próprio não pode montar em dobro.
      -->
      <template v-if="umaPagina">
        <div v-show="!virando" class="fish-book__pagina fish-book__pagina--unica">
          <slot name="esquerda" />
          <slot name="direita" />
        </div>
      </template>

      <template v-else>
        <div v-show="!virando" class="fish-book__pagina fish-book__pagina--esquerda">
          <slot name="esquerda" />
        </div>

        <div v-show="!virando" class="fish-book__pagina fish-book__pagina--direita">
          <slot name="direita" />
        </div>
      </template>
    </div>

    <!-- Sem o que virar (a mochila, uma folha avulsa), as setas nem aparecem: duas setas apagadas
         permanentes seriam a promessa de páginas que não existem. -->
    <div v-if="temAnterior || temProxima || paginacao" class="fish-book__setas">
      <button class="fish-book__seta" type="button" :disabled="!temAnterior" aria-label="Página anterior" @click="virar('esq')">
        <FishIcon nome="chevron-left" tamanho="pequeno" />
      </button>
      <span v-if="paginacao" class="fish-book__paginacao t-numero">{{ paginacao }}</span>
      <button class="fish-book__seta" type="button" :disabled="!temProxima" aria-label="Próxima página" @click="virar('dir')">
        <FishIcon nome="chevron-right" tamanho="pequeno" />
      </button>
    </div>
  </div>
</template>

<script>
import FishIcon from './FishIcon.vue';

/** Quanto dura cada quadro da virada. 5 quadros × 55ms = 275ms — perto de `--t-medio`. */
const MS_POR_QUADRO = 55;

/**
 * 6.1 — `FishBook`: a dupla de páginas, os marcadores e a virada (D3, D9).
 *
 * ## Onde o livro é a forma certa, e onde ele atrapalha (D3)
 *
 * **Certo:** Bestiário (é literalmente um caderno de campo), Missões e Conquistas (diário de bordo),
 * Inventário (equipamento numa página, mochila na outra).
 *
 * **Atrapalha:** Pescar (precisa ser imersiva e sem moldura), Loja (já tem uma cena boa, com o Velho
 * João), HUD (moldura em HUD é ruído).
 *
 * ## O limite que o War Mages nunca precisou resolver: o telefone
 *
 * Lá é Electron, só desktop. Aqui é web, com breakpoint até 360px, onde uma dupla de páginas não
 * cabe. Então **o livro é a apresentação de tablet e desktop, e no telefone degrada para uma página
 * só**, com os marcadores laterais virando barra superior. O `umaPagina` é medido por `matchMedia`,
 * não chutado por largura de janela em `data`.
 *
 * ## O que não se herda do War Mages (D9)
 *
 * **A abertura é mais seca:** lá são cinco quadros de cerimônia, aqui *"o caderno já está na mão"*.
 * Os cinco quadros existem no recorte e este componente usa **dois** — o suficiente para não ser um
 * corte, longe da cerimônia.
 *
 * **As abas viram fitas.** Marcador de fita e etiqueta, não aba gótica lateral.
 *
 * **A virada usa 5 dos 9 quadros** (1, 3, 5, 7, 9): nove a 55ms seriam meio segundo só para trocar
 * de página, e o D9 pede seco.
 */
export default {
  name: 'FishBook',
  components: { FishIcon },
  props: {
    /** `[{ id, rotulo }]` — os marcadores de fita. */
    marcadores: { type: Array, default: () => [] },
    marcador: { type: [String, Number], default: null },
    rotuloDosMarcadores: { type: String, default: 'Seções do caderno' },
    temAnterior: { type: Boolean, default: false },
    temProxima: { type: Boolean, default: false },
    /** Ex.: "12 / 30". Fica entre as setas. */
    paginacao: { type: String, default: '' },
  },
  emits: ['update:marcador', 'virar'],
  data() {
    return { umaPagina: false, virando: false, quadro: 0, sentido: 'dir' };
  },
  computed: {
    arteAtual() {
      // A página única do telefone. A virada nunca anima aqui (ver `virar`), então não há quadros.
      if (this.umaPagina) return '/ui/caderno-pagina.png';
      if (!this.virando) return '/ui/caderno-repouso.png';
      const lado = this.sentido === 'dir' ? 'dir' : 'esq';
      return `/ui/caderno-vira-${lado}-${Math.min(5, Math.max(1, this.quadro))}.png`;
    },
  },
  mounted() {
    this.medir();
    window.addEventListener('resize', this.medir, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.medir);
    if (this.relogio) clearInterval(this.relogio);
  },
  methods: {
    medir() {
      // 600px é o breakpoint `tablet` dos quatro (UI-08): abaixo dele a dupla não cabe.
      this.umaPagina = !window.matchMedia('(min-width: 600px)').matches;
    },
    /**
     * A virada, quadro a quadro.
     *
     * O evento `virar` é emitido **no meio** da animação, não no fim: é quando a página cobre o
     * conteúdo, então é a hora de trocá-lo. Emitir no fim faria o conteúdo novo aparecer depois de
     * a página já ter virado — e o jogador veria o conteúdo antigo por um quadro.
     */
    virar(sentido) {
      if (this.virando) return;
      if (sentido === 'dir' && !this.temProxima) return;
      if (sentido === 'esq' && !this.temAnterior) return;

      // `prefers-reduced-motion`: a virada some, a troca de página fica. A animação é decorativa; a
      // navegação não é. No telefone idem — a página única não tem a arte dos quadros da virada.
      if (this.umaPagina || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.$emit('virar', sentido);
        return;
      }

      this.sentido = sentido;
      this.virando = true;
      this.quadro = 1;

      this.relogio = setInterval(() => {
        this.quadro += 1;
        if (this.quadro === 3) this.$emit('virar', sentido);
        if (this.quadro > 5) {
          clearInterval(this.relogio);
          this.relogio = null;
          this.virando = false;
          this.quadro = 0;
        }
      }, MS_POR_QUADRO);
    },
  },
};
</script>

<style scoped>
.fish-book {
  --fita-1: var(--cor-ruim);
  --fita-2: var(--ouro);
  --fita-3: var(--cor-bom);
  --fita-4: var(--raridade-rara);
  --fita-5: var(--raridade-epica);

  display: flex;
  flex-direction: column;
  gap: var(--e-2);
  align-items: center;
}

/* As fitas: barra em cima no telefone, coluna na lateral no tablet e no desktop (D3). */
.fish-book__fitas {
  display: flex;
  gap: var(--e-1);
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none;
}

.fish-book__fitas::-webkit-scrollbar {
  display: none;
}

.fish-book__fita {
  flex-shrink: 0;
  min-height: var(--alvo-minimo);
  padding: var(--e-1) var(--e-3);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  background: var(--papel-sombra);
  border: 2px solid var(--madeira-escura);
  border-bottom-width: 5px;
  border-bottom-color: var(--cor-fita);
  transition: background var(--t-rapido) var(--curva-suave), color var(--t-rapido) var(--curva-suave),
    transform var(--t-rapido) var(--curva-quique);
}

/* A fita responde à mão: desliza um tico para fora do caderno no hover, afunda no clique. */
@media (hover: hover) {
  .fish-book__fita:hover {
    transform: translateY(-2px);
  }
}

.fish-book__fita:active {
  transform: translateY(1px);
}

.fish-book__fita--ativa {
  background: var(--papel-frio);
  color: var(--tinta);
}

.fish-book__caderno {
  position: relative;
  width: 100%;
  /* O teto é 2× o recorte (672×520) — escala INTEIRA, a única em que o pixel não se deforma. Quem
   * usa o livro baixa esse teto por `--livro-largura-max`; quem não mexe fica com 1008 (1,5×),
   * que era o valor único de antes. */
  max-width: var(--livro-largura-max, 1008px);
  /* A proporção do recorte. `aspect-ratio` em vez de altura fixa: é o UI-17 aplicado —
   * altura fixa somada a texto que cresce é exatamente o que cortava texto no cliente antigo. */
  aspect-ratio: 672 / 520;
}

/* A página única do telefone: a arte é a metade direita (336×520) do mesmo caderno, então as
 * réguas e os ornamentos são os da página direita menos os 336 da esquerda — quadro em x 21..313,
 * caixa de conteúdo em x 42..292 → 12,5% a 86,9%. O y é o mesmo da dupla. */
.fish-book--uma-pagina .fish-book__caderno {
  aspect-ratio: 336 / 520;
  max-width: 420px;
  margin: 0 auto;
}

.fish-book--uma-pagina .fish-book__pagina--unica {
  left: 12.5%;
  width: 74.4048%;
}

/**
 * A FAIXA MORTA DO TOPO — 19,8% da arte, e ela é aproveitável.
 *
 * O canvas é 672×520 mas a tinta só começa em `y=103` (medido no PNG, não estimado): o quinto de
 * cima do caderno é transparente, em TODOS os quadros. Numa página com cabeçalho fixo e título por
 * cima do livro, esses ~100px de ar podem ficar POR BAIXO deles em vez de somar altura — é o que a
 * tela do Inventário faz, e é de lá que sai a diferença entre um livro de 850px e um de 1070px na
 * mesma janela. Quem repetir o truque mede de novo se a arte for recortada outra vez.
 */
.fish-book__arte {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  image-rendering: pixelated;
}

/**
 * As áreas de página, em percentual da arte.
 *
 * **O limite é o QUADRO DESENHADO, não a borda do papel.** Cada página tem uma régua fina e quatro
 * ornamentos de canto, e a primeira versão mediu a folha inteira (`y 104..489`, esquerda
 * `x 28..319`): o texto encostava na régua e passava por cima dos cantos. As réguas estão medidas
 * em `caderno-repouso.png` — verticais em `x 27` e `x 319` (esquerda), `x 357` e `x 649` (direita);
 * horizontais em `y 115` e `y 479`, a de cima em ARCO, que desce até `y 122` nas pontas.
 *
 * O recuo de **20px de arte** para dentro da régua não é gosto: é exatamente o que livra os
 * ornamentos, que ocupam 19px em cada canto (`x 28..46` e `x 300..318`, `y 116..134` e `y 460..478`).
 * Sobra a caixa `x 48..298` │ `y 130..471` na esquerda e `x 378..628` na direita — conferido pixel a
 * pixel: zero pixel de tinta da arte dentro dela.
 *
 * Em percentual de 672×520: esquerda `x` 7,1429% + 37,2024%; direita 56,25% + 37,2024%; `y` 25% a
 * 90,5769% (topo 25%, base 9,4231%). **Recortar a arte de novo exige remedir.**
 */
.fish-book__pagina {
  position: absolute;
  top: 25%;
  bottom: 9.4231%;
  overflow-y: auto;
  /* Sem padding: a margem inteira já está na geometria acima, medida contra a arte. Somar padding
   * aqui empurraria o conteúdo para dentro de um quadro que já está no lugar certo. */
  padding: 0 15px 0 5px;
  color: var(--tinta);
  font-size: var(--f-2);
  /* A entrada da página (na montagem e na troca de fita, via :key): surge do papel, curta. */
  animation: fish-book-surgir var(--t-medio, 0.25s) var(--curva-suave) both;
}

@keyframes fish-book-surgir {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fish-book__pagina {
    animation: none;
  }
}

.fish-book__pagina--esquerda {
  left: 7.1429%;
  width: 38.2024%;
}

.fish-book__pagina--direita {
  left: 56.25%;
  width: 38.2024%;
}

.fish-book__setas {
  display: flex;
  align-items: center;
  gap: var(--e-4);
}

.fish-book__seta {
  color: var(--papel);
  padding: var(--e-2);
}

.fish-book__seta:disabled {
  opacity: 0.3;
}

.fish-book__paginacao {
  color: var(--texto-fraco);
  font-size: var(--f-2);
  min-width: 6ch;
  text-align: center;
}

@media (min-width: 600px) {
  .fish-book {
    flex-direction: row;
    align-items: flex-start;
  }

  /**
   * O LIVRO É LIMITADO PELOS DOIS EIXOS, e no desktop quem manda é a ALTURA.
   *
   * Uma tela larga e baixa — 1920×1080 é quase 2:1, o livro é 1,29:1 — sobra largura e falta
   * altura. Um `max-width` fixo, sozinho, dava um livro que **sempre** cabia na largura e
   * **nunca** usava a altura que havia: o que o autor descreveu como "em fullhd praticamente
   * some" (03/09).
   *
   * O `min()` é obrigatório, e não é gosto: com `aspect-ratio`, um `max-height` clampa a altura e
   * **deixa a largura onde estava** — quebra a proporção em vez de conter o livro. Conter pelos
   * dois eixos exige converter a altura disponível em largura (× 672/520) e ficar com a menor.
   *
   * `--livro-reserva` é a altura que a página gastou antes do livro (cabeçalho, barra, respiro).
   * Quem não declara fica com 200px, folgado de propósito.
   */
  .fish-book__caderno {
    max-width: min(
      var(--livro-largura-max, 1008px),
      calc((100dvh - var(--livro-reserva, 200px)) * 672 / 520)
    );
  }

  .fish-book__fitas {
    flex-direction: column;
    width: auto;
    /* As duas colunas descem junto com o papel. O padrão (--e-8) alinha com o livro em fluxo
     * normal; quem sobe o livro por baixo de um cabeçalho fixo passa o recuo dele aqui. */
    padding-top: var(--livro-recuo-do-topo, var(--e-8));
  }

  .fish-book__fita {
    border-bottom-width: 2px;
    border-left-width: 5px;
    border-left-color: var(--cor-fita);
    text-align: left;
  }

  .fish-book__setas {
    flex-direction: column;
    padding-top: var(--livro-recuo-do-topo, var(--e-8));
  }
}
</style>
